// Modules tab — grid of ModuleCards for a tenant's TenantConfig.
// Optimistic state: local copy of modules map, updated on each toggle.

import { useState } from "react";
import type { JSX } from "react";
import type { TenantConfig, ModuleKey, ModuleState } from "../lib/adminApiClient.js";
import { MODULE_ORDER } from "../lib/modulesMeta.js";
import { ModuleCard } from "./ModuleCard.js";

interface Props { tenantId: string; config: TenantConfig; }

export function ModulesTab({ tenantId, config }: Props): JSX.Element {
  const [modules, setModules] = useState<Record<ModuleKey, ModuleState>>(config.modules);

  function onChanged(key: ModuleKey, active: boolean): void {
    setModules((prev) => ({ ...prev, [key]: { ...prev[key], active } }));
  }

  // Pair each ordered key with its (definitely-present) state. The filter
  // narrows away keys whose state is undefined; downstream code can read
  // `state` without a non-null assertion.
  const present  = MODULE_ORDER.flatMap((k) => {
    const state = modules[k];
    return state ? [{ k, state }] : [];
  });
  const active   = present.filter(({ state }) => state.active);
  const inactive = present.filter(({ state }) => !state.active);

  return (
    <div className="space-y-6">
      {active.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
            Active modules ({active.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {active.map(({ k, state }) => (
              <ModuleCard
                key={k} tenantId={tenantId} moduleKey={k}
                state={state} onChanged={onChanged}
              />
            ))}
          </div>
        </section>
      )}
      {inactive.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
            Inactive modules ({inactive.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {inactive.map(({ k, state }) => (
              <ModuleCard
                key={k} tenantId={tenantId} moduleKey={k}
                state={state} onChanged={onChanged}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
